import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { CenteredSpinner } from '../components/Spinner.tsx';
import { NavIcon } from '../components/NavIcon.tsx';
import { useAdminAuth } from '../lib/auth/admin.tsx';
import { useBranding } from '../lib/branding.tsx';

const ACCENT = '#2B93FF';
const ACCENT_LIGHT = '#E8F4FF';

function initials(name: string | undefined): string {
  if (!name) return 'U';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return 'U';
  const first = parts[0] ?? '';
  if (parts.length === 1) return first.slice(0, 2).toUpperCase() || 'U';
  const last = parts[parts.length - 1] ?? '';
  return `${first[0] ?? ''}${last[0] ?? ''}`.toUpperCase() || 'U';
}

// Minimal chrome for the auditor persona: branding header + sign-out, no admin
// navigation or team-region controls. Auditors only ever see their assigned audits.
export function AuditorShell() {
  const { user, loading, signOut } = useAdminAuth();
  const { branding } = useBranding();
  const location = useLocation();

  if (loading) return <CenteredSpinner />;
  if (!user) return <Navigate to="/admin/login" state={{ from: location }} replace />;
  // Staff belong in the admin portal, not here.
  if (user.role !== 'auditor') return <Navigate to="/admin" replace />;

  const brandName = branding.companyName?.replace(/ Safety.*$/i, '') ?? 'Locate2u';

  return (
    <div className="flex min-h-screen flex-col" style={{ background: '#F8FAFC' }}>
      <header
        className="flex items-center gap-4 bg-white px-6 py-4 sm:px-8"
        style={{ borderBottom: '1px solid #E2E8F0' }}
      >
        {branding.logoUrl ? (
          <img src={branding.logoUrl} alt={brandName} className="h-7 w-auto" />
        ) : (
          <span className="text-[16px] font-bold tracking-tight" style={{ color: '#1E293B' }}>
            {brandName}
          </span>
        )}
        <div className="h-6 w-px" style={{ background: '#E2E8F0' }} />
        <div className="text-[15px] font-medium" style={{ color: '#64748B' }}>
          Auditor Portal
        </div>

        <button
          type="button"
          onClick={() => void signOut()}
          className="ml-auto flex items-center gap-2 rounded-lg p-1.5 text-left transition hover:bg-slate-50"
          title={`Sign out ${user.email}`}
        >
          <div
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-[12px] font-bold"
            style={{ background: ACCENT_LIGHT, color: ACCENT }}
          >
            {initials(user.name)}
          </div>
          <div className="hidden min-w-0 sm:block">
            <div className="truncate text-[13px] font-semibold" style={{ color: '#1E293B' }}>
              {user.name}
            </div>
            <div className="truncate text-[11px]" style={{ color: '#64748B' }}>
              Auditor
            </div>
          </div>
          <NavIcon name="log-out" size={16} style={{ color: '#94A3B8' }} />
        </button>
      </header>

      <main className="flex-1 overflow-y-auto">
        <div className="mx-auto max-w-4xl px-6 py-6 sm:px-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
